import {
  CalculatedEnergyLabelDTO,
  EnergyLabelTypeDTO,
  IndexTypeDTO,
  LloydsBuildingRoadmapDTO,
  LloydsRoadmapBuildingYearDTO,
  LloydsRoadmapImplementedMeasureDTO,
  LloydsRoadmapPlannedMeasureDTO,
  LloydsRoadmapPortfolioBuildingDTO,
  LloydsRoadmapPortfolioDetailsDTO,
  MeasureCodeDTO,
  PortfolioBuildingsRoadmapBuildingDTO,
  PortfolioBuildingsRoadmapBuildingDTORoadmap,
  PortfolioBuildingsRoadmapBuildingDTORoadmapImplementedMeasures,
  PortfolioBuildingsRoadmapBuildingDTORoadmapPlannedMeasures,
  PortfolioBuildingsRoadmapBuildingDTORoadmapYears,
  PortfolioBuildingsRoadmapDTO,
  PortfolioBuildingsRoadmapDTOBusinessCase,
  PortfolioBuildingsRoadmapDTOBusinessCaseForBuildingsWithRoadmap,
  PortfolioBuildingsRoadmapDTOTotalInvestmentPerYear
} from 'csr-asset-app';

/**
 * Maps Lloyds RETLL report payload to the shared PortfolioBuildingsRoadmapDTO shape
 * so existing report page/table components can be reused.
 */
export function mapLloydsRoadmapToPortfolioBuildingsRoadmap(
  lloyds: LloydsRoadmapPortfolioDetailsDTO
): PortfolioBuildingsRoadmapDTO {
  return {
    currencyCode: lloyds.currencyCode,
    businessCase: {
      numberOfBuildings: lloyds.businessCase.numberOfBuildings,
      totalArea: lloyds.businessCase.totalArea
    } satisfies PortfolioBuildingsRoadmapDTOBusinessCase,
    businessCaseForBuildingsWithRoadmap: {
      totalInvestment: lloyds.businessCase.totalInvestment,
      totalCostReduction: lloyds.businessCase.totalCostReduction,
      totalYearlyCo2ConsumptionReduction: lloyds.businessCase.totalYearlyCo2ConsumptionReduction,
      co2ConsumptionReductionPercentage: lloyds.businessCase.co2ConsumptionReductionPercentage
    } satisfies PortfolioBuildingsRoadmapDTOBusinessCaseForBuildingsWithRoadmap,
    labelDistribution: lloyds.labelDistribution,
    labelsCertified: lloyds.labelsCertified,
    totalInvestmentPerYear: lloyds.totalInvestmentPerYear.map(
      (year): PortfolioBuildingsRoadmapDTOTotalInvestmentPerYear => ({
        year: year.year,
        investment: year.investment,
        containsUserDefined: year.containsUserDefined
      })
    ),
    buildings: lloyds.buildings.map(mapBuilding)
  };
}

function mapBuilding(building: LloydsRoadmapPortfolioBuildingDTO): PortfolioBuildingsRoadmapBuildingDTO {
  return {
    building: {
      id: building.id,
      displayAddress: building.displayAddress,
      countryCode: building.countryCode,
      objectType: building.objectType,
      year: building.year,
      constructionPeriod: building.constructionPeriod,
      monument: building.monument
    },
    energyLabels: building.energyLabels,
    roadmap: building.roadmap ? mapRoadmap(building.roadmap) : undefined,
    registeredEnergyLabels: building.registeredEnergyLabels
  };
}

function mapRoadmap(roadmap: LloydsBuildingRoadmapDTO): PortfolioBuildingsRoadmapBuildingDTORoadmap {
  return {
    totalInvestmentPlannedMeasures: roadmap.totalInvestmentPlannedMeasures,
    years: roadmap.years.map(mapYear),
    plannedMeasures: roadmap.plannedMeasures.map(mapPlannedMeasure),
    implementedMeasures: roadmap.implementedMeasures.map(mapImplementedMeasure)
  };
}

function mapYear(year: LloydsRoadmapBuildingYearDTO): PortfolioBuildingsRoadmapBuildingDTORoadmapYears {
  return {
    year: year.year,
    investment: year.investment,
    containsUserDefined: year.containsUserDefined,
    improvedEnergyLabel: year.improvedEnergyLabel
      ? ({
          label: year.improvedEnergyLabel.label,
          index: year.improvedEnergyLabel.index,
          indexType: year.improvedEnergyLabel.indexType as IndexTypeDTO,
          labelType: year.improvedEnergyLabel.labelType as EnergyLabelTypeDTO
        } satisfies CalculatedEnergyLabelDTO)
      : undefined
  };
}

function mapPlannedMeasure(
  measure: LloydsRoadmapPlannedMeasureDTO
): PortfolioBuildingsRoadmapBuildingDTORoadmapPlannedMeasures {
  return {
    year: measure.year,
    investment: measure.investment,
    isUserDefined: measure.isUserDefined,
    measureCode: measure.measureCode as MeasureCodeDTO | undefined,
    measureName: measure.measureName
  };
}

function mapImplementedMeasure(
  measure: LloydsRoadmapImplementedMeasureDTO
): PortfolioBuildingsRoadmapBuildingDTORoadmapImplementedMeasures {
  return {
    year: measure.year,
    co2ConsumptionReduction: measure.co2ConsumptionReduction,
    measureCode: measure.measureCode as MeasureCodeDTO | undefined,
    measureName: measure.measureName
  };
}
